<template>
    <div class="service-package-inner">
        <ul class="service-package-inner-rows">
            <ServicePackageUSP
                v-for="(usp, index) in usps"
                :key="index"
                :usp="usp"
                :showDescription="showUSPDescription" />
        </ul>

        <div v-if="!isLargeDesktop" class="service-package-inner-expand">
            <div v-if="showExpandButton" class="service-package-inner-expand-button">
                <ButtonLink
                    @click="toggleExpand"
                    size="m"
                    iconPosition="right"
                    icon="Chevron"
                    :iconRotate="isExpanded ? 180 : 0">
                    {{ t({ key: isExpanded ? 'shared.show_less' : 'shared.show_more' }) }}
                </ButtonLink>
            </div>

            <Link
                v-if="isExpanded || isTablet"
                :href="link || undefined"
                :label="t({ key: 'shared.read_more' })"
                target="_blank"
                class="service-package-inner-link"
                @click="handleReadMore" />
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, ref, computed } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { logStatsigCheckoutServicePackageInteraction } from '@/tracking/statsig/checkout.functions';
    import {
        ServicePackageName,
        ServicePackageUSP as USP,
    } from '@ui/components/ServicePackage/ServicePackage.interfaces';
    import ServicePackageUSP from './ServicePackageUSP.vue';
    import ButtonLink from '../../Button/ButtonLink.vue';
    import Link from '../../Link/Link.vue';

    export default defineComponent({
        name: 'ServicePackageUSPList',
        methods: { t },
        components: {
            ButtonLink,
            Link,
            ServicePackageUSP,
        },
        props: {
            usps: {
                type: Object as PropType<USP[]>,
                required: true,
            },
            name: {
                type: String as PropType<ServicePackageName>,
                required: true,
            },
            link: {
                type: String as PropType<string | null>,
                required: true,
            },
        },
        setup(props) {
            const isExpanded = ref(false);
            const { isTablet, isDesktop, isLargeDesktop } = useScreenSize();

            const showUSPDescription = computed(
                () => isTablet.value || isExpanded.value || !props.usps.every((u) => u.title)
            );

            const showExpandButton = computed(
                () => !isTablet.value && props.usps.some((u) => u.title && u.text)
            );

            const handleShowMore = () => {
                logStatsigCheckoutServicePackageInteraction('ShowMore', props.name);
            };

            const handleReadMore = () => {
                logStatsigCheckoutServicePackageInteraction('ReadMore', props.name);
            };

            const toggleExpand = () => {
                const wasExpanded = isExpanded.value;
                isExpanded.value = !isExpanded.value;

                if (!wasExpanded && isExpanded.value) {
                    handleShowMore();
                }
            };

            return {
                isTablet,
                isDesktop,
                isLargeDesktop,
                isExpanded,
                showUSPDescription,
                showExpandButton,
                toggleExpand,
                handleReadMore,
            };
        },
    });
</script>

<style scoped lang="scss">
    @forward '../ServicePackage.scss';
</style>
